/**
 * Provider sign-in and confirmation on the web. Each is a top-level navigation
 * to the API's root routes (social sign-in PRD, D3): the API sends the browser
 * on to Google or Apple and back again, which no fetch or XHR could follow.
 */

import { API_ENDPOINTS, isSocialProvider } from '@personal-trainer-app/shared';
import { getAuthBaseUrl } from '@personal-trainer-app/shared/src/config/api';

import type { SocialProvider } from '@personal-trainer-app/shared';

/**
 * What the website says when a provider flow lapsed: unlike in the apps, it
 * may also have finished in another tab.
 */
export const EXPIRED_ON_THE_WEB =
  'It took too long, or finished in another tab. Try again.';

/** A provider's route on the API root: `path` with `{provider}` filled in. */
function providerRouteUrl(path: string, provider: SocialProvider): string {
  return `${getAuthBaseUrl()}${path.replace('{provider}', provider)}`;
}

/** Where signing in with a provider starts: `/auth/{provider}/redirect` on the API. */
export function socialSignInUrl(provider: SocialProvider): string {
  return providerRouteUrl(API_ENDPOINTS.SOCIAL_REDIRECT.path, provider);
}

/**
 * Where confirming it's you with a provider starts: `/auth/{provider}/confirm`
 * on the API. It ends on Settings with `account_flow=confirm` and a `result`.
 */
export function socialConfirmUrl(provider: SocialProvider): string {
  return providerRouteUrl(API_ENDPOINTS.SOCIAL_CONFIRM.path, provider);
}

/**
 * The provider this tab last left to sign in with. The API's return to
 * `/login` doesn't name it, and some words need it: "a different Apple
 * account" is only right after an Apple sign-in.
 */
const LAST_PROVIDER_KEY = 'social-sign-in:provider';

/** Remembers the provider this tab is leaving to sign in with. */
export function rememberSocialSignInProvider(provider: SocialProvider): void {
  try {
    sessionStorage.setItem(LAST_PROVIDER_KEY, provider);
  } catch {
    // Storage is full or blocked: the words fall back to naming neither.
  }
}

/** Leaves the app to sign in with a provider, remembering which. */
export function startSocialSignIn(provider: SocialProvider): void {
  rememberSocialSignInProvider(provider);
  window.location.href = socialSignInUrl(provider);
}

/**
 * The provider this tab last left to sign in with, or null. Reading leaves it
 * in place, so a render can read it twice; `forgetSocialSignInProvider` drops
 * it once the return has been handled.
 */
export function lastSocialSignInProvider(): SocialProvider | null {
  try {
    const provider = sessionStorage.getItem(LAST_PROVIDER_KEY);
    return isSocialProvider(provider) ? provider : null;
  } catch {
    return null;
  }
}

/** Forgets the provider this tab left to sign in with. */
export function forgetSocialSignInProvider(): void {
  try {
    sessionStorage.removeItem(LAST_PROVIDER_KEY);
  } catch {
    // Nothing was kept.
  }
}

/** Leaves the app to confirm it's you with a provider. */
export function startSocialConfirm(provider: SocialProvider): void {
  window.location.href = socialConfirmUrl(provider);
}

/**
 * Where connecting a provider starts: `/auth/{provider}/link` on the API. It
 * ends on Settings with `account_flow=link` and a `result`.
 */
export function socialLinkUrl(provider: SocialProvider): string {
  return providerRouteUrl(API_ENDPOINTS.SOCIAL_LINK.path, provider);
}

/** Leaves the app to connect a provider. */
export function startSocialLink(provider: SocialProvider): void {
  window.location.href = socialLinkUrl(provider);
}
